import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import DeferredToasters from "@/components/DeferredToasters";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { LanguageSync } from "@/components/LanguageSync";
import ScrollToTop from "@/components/ScrollToTop";
import PointerEventsGuard from "@/components/PointerEventsGuard";
import InternalFooter from "@/components/InternalFooter";
import PublicShell from "@/components/public/PublicShell";
import CookieConsent from "@/components/CookieConsent";
import { CONSENT_DEFAULT_SCRIPT } from "@/lib/consent";
import ErrorBoundaryWithLogger from "@/components/ErrorBoundaryWithLogger";
import NotFound from "@/pages/NotFound";
import { usePostAuthRedirect } from "@/hooks/usePostAuthRedirect";
import { useRestorePendingDiagnosis } from "@/hooks/useRestorePendingDiagnosis";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import { usePublicPageViews } from "@/hooks/usePublicPageViews";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { detectIndexableHost } from "@/lib/panel-host-detect";
import { isPrivatePath } from "@/lib/private-routes";
import { installCheckoutSessionGuard } from "@/lib/checkoutSessionGuard";
// La hoja de estilos se importa de forma normal: Vite la emite como archivo
// minificado y versionado que el navegador cachea. Incrustarla en el HTML
// (?inline) metía ~370 KB de CSS en cada respuesta y retrasaba el primer
// pintado en móvil.
import "../styles.css";


// ported from main.tsx — build fingerprint + stale-chunk recovery (browser only)
const nukeCache = async () => {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* noop */
  }
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    /* noop */
  }
};

const RELOAD_KEY = "lemon_stale_reload_done";

/**
 * Recarga silenciosa cuando el navegador pide un trozo de página que ya no
 * existe (se publicó una versión nueva con la pestaña abierta). Solo una vez
 * por pestaña: si vuelve a fallar se muestra el error controlado.
 */
const tryRecoverFromStaleChunk = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  void nukeCache().then(() => window.location.reload());
  return true;
};

/**
 * El fallo de un trozo de página caducado llega con mensajes muy distintos:
 * el import falla, o resuelve a `undefined` y el router revienta al leer
 * `component` / `errorComponent` de la ruta.
 */
const isStaleChunkError = (msg: string) =>
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|_result\.default|Unable to preload CSS|Unable to preload module|reading 'component'|reading 'errorComponent'|reading 'pendingComponent'|reading 'notFoundComponent'/i.test(
    msg,
  );

if (typeof window !== "undefined") {
  const BUILD_ID = import.meta.env.VITE_BUILD_TIME || "__dev__";
  console.info(`%c[Lemon] Build: ${BUILD_ID}`, "color:#FFE143;font-weight:bold");
  document.documentElement.setAttribute("data-build", String(BUILD_ID));

  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    tryRecoverFromStaleChunk();
  });

  window.addEventListener("error", (e) => {
    if (e?.message && isStaleChunkError(e.message)) tryRecoverFromStaleChunk();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e?.reason && (e.reason.message || String(e.reason))) || "";
    if (isStaleChunkError(msg)) tryRecoverFromStaleChunk();
  });
}

const FONT_FACE_CSS = `@font-face{font-family:'Inter Fallback';src:local('Arial');size-adjust:106%;ascent-override:91.4%;descent-override:22.8%;line-gap-override:0%}
@font-face{font-family:'Cormorant Fallback';src:local('Times New Roman');size-adjust:95%;ascent-override:99%;descent-override:28.4%;line-gap-override:0%}
@font-face{font-family:'Inter';font-style:normal;font-weight:400 900;font-display:swap;src:url(/fonts/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.woff2) format('woff2')}
@font-face{font-family:'Cormorant Garamond';font-style:normal;font-weight:400 700;font-display:swap;src:url(/fonts/co3umX5slCNuHLi8bLeY9MK7whWMhyjypVO7abI26QOD_hg9GnM.woff2) format('woff2')}
@font-face{font-family:'Figtree';font-style:normal;font-weight:400 700;font-display:swap;src:url(/fonts/figtree-latin.ttf) format('truetype')}
@font-face{font-family:'Outfit';font-style:normal;font-weight:600 700;font-display:swap;src:url(/fonts/outfit-latin.ttf) format('truetype')}
@font-face{font-family:'IBM Plex Sans Figures';font-style:normal;font-weight:400 500;font-display:swap;src:url(/fonts/ibm-plex-sans-latin-400.woff2) format('woff2')}
@font-face{font-family:'IBM Plex Sans Figures';font-style:normal;font-weight:600 700;font-display:swap;src:url(/fonts/ibm-plex-sans-latin-700.woff2) format('woff2')}`;

const SW_KILLSWITCH_SCRIPT = `(function(){try{if('serviceWorker' in navigator){navigator.serviceWorker.getRegistrations().then(function(rs){rs.forEach(function(r){r.unregister();});}).catch(function(){});}if(typeof caches!=='undefined'&&caches.keys){caches.keys().then(function(ks){ks.forEach(function(k){caches.delete(k);});}).catch(function(){});}}catch(_){}})();`;

// GTM se carga al inicio: Consent Mode v2 ya bloquea cualquier almacenamiento
// hasta que el visitante acepta, y cargar pronto permite que GA4 vea las UTM
// y el gclid de la URL de entrada. La etiqueta de GA4 la dispara el contenedor.
const ANALYTICS_SCRIPT = `(function(){
window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};
var tt='external';try{if(localStorage.getItem('rl-analytics-admin')==='1'||/(?:^|; )rl_team=1/.test(document.cookie))tt='internal';}catch(_){}
window.gtag('set',{traffic_type:tt});window.dataLayer.push({traffic_type:tt});
if(tt==='internal')return;
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-WV4XHRRD');
window.dataLayer=window.dataLayer||[];
})();`;

const PostAuthRouter = () => {
  usePostAuthRedirect();
  useRestorePendingDiagnosis();
  useActivityTracker();
  usePublicPageViews();
  return null;
};

function RootShell({ children }: { children: React.ReactNode }): React.ReactElement {
  // El idioma del documento se deriva de la URL en el servidor: cualquier ruta
  // bajo /en debe servirse como en-US para rastreadores y lectores de pantalla.
  // Incluimos dir="ltr" como parte básica del setup de accesibilidad del documento.
  const pathname: string = useRouterState({ select: (s) => s.location.pathname });
  const isEnglish: boolean = pathname === "/en" || pathname.startsWith("/en/");
  return isEnglish ? (
    <html lang="en" dir="ltr" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WV4XHRRD"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  ) : (
    <html lang="es" dir="ltr" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WV4XHRRD"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  useEffect(() => {
    installCheckoutSessionGuard();
    // Tras una carga correcta, libera la marca para la próxima publicación.
    const t = window.setTimeout(() => {
      try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ }
    }, 5000);
    return () => window.clearTimeout(t);
  }, []);
  // Clics en las 5 páginas de captación (landings + herramientas), cargado en diferido.
  useEffect(() => {
    let stop: (() => void) | undefined;
    void import("@/lib/captureClickTracking").then((m) => { stop = m.startCaptureClickTracking(); });
    return () => stop?.();
  }, []);
  return (
    <HelmetProvider context={{}}>
      <ErrorBoundaryWithLogger>
        <RootProviders />
      </ErrorBoundaryWithLogger>
    </HelmetProvider>
  );
}

/**
 * ¿Esta ruta debe llevar la envoltura pública (menú lateral + barra superior)?
 * La portada y el panel privado ya montan su propia estructura.
 */
const usesPublicShell = (pathname: string): boolean => {
  const p = pathname.replace(/\/$/, "") || "/";
  const bare = new Set([
    "/dashboard", "/en/dashboard",
    // Secciones del panel público: pintan su propio menú lateral.
    "/agente-retail-ia", "/en/retail-ai-agent",
    "/hoja-de-ruta", "/en/retail-roadmap",
    "/herramientas/mis-locales", "/en/tools/my-premises",
    "/herramientas/calculadora", "/en/tools/calculator",
    "/proveedores", "/en/suppliers",
    "/consejos", "/en/tips",
    "/casos-reales", "/en/real-cases",
    // Landing del test de supervivencia: cabecera y pie propios.
    "/test-supervivencia", "/en/retail-survival-test",

  ]);
  if (bare.has(p)) return false;
  if (p.startsWith("/admin")) return false;
  if (p.startsWith("/auth")) return false;
  if (p.startsWith("/api")) return false;
  if (p.startsWith("/lp/") || p.startsWith("/en/lp/")) return false;
  return true;

};

function RootProviders() {
  const { queryClient } = Route.useRouteContext();
  const pathname: string = useRouterState({ select: (s) => s.location.pathname });
  const withShell = usesPublicShell(pathname);
  // El router puede conservar la barra final en algunas entradas directas.
  // Normalizarla evita que una misma página reciba dos pies por variar la URL.
  const footerPath = pathname.replace(/\/+$/, "") || "/";
  const hideGlobalFooter =
    footerPath === "/lp/instagram" || footerPath === "/en/lp/instagram" ||
    // Detector de fugas: pie de captación propio.
    footerPath === "/detector-de-fugas" ||
    footerPath === "/en/leak-detector" ||
    footerPath === "/lp/test-supervivencia" ||
    footerPath === "/en/retail-survival-test" ||
    // Back office: sin pie.
    footerPath === "/admin" || footerPath.startsWith("/admin/") ||
    // Llevan su propio pie corporativo completo.
    footerPath === "/profesionales-retail" ||
    footerPath === "/en/retail-experts" ||
    // Vistas del panel público: ya montan su propia franja legal.
    footerPath === "/hoja-de-ruta" ||
    footerPath === "/en/retail-roadmap" ||
    footerPath === "/herramientas/mis-locales" ||
    footerPath === "/en/tools/my-premises" ||
    footerPath === "/herramientas/calculadora" ||
    footerPath === "/en/tools/calculator" ||
    footerPath === "/herramientas/kpis" ||
    footerPath === "/en/tools/kpis" ||
    footerPath === "/herramientas" ||
    footerPath === "/en/tools" ||
    footerPath.startsWith("/herramientas/") ||
    footerPath.startsWith("/en/tools/") ||

    footerPath === "/proveedores" ||
    footerPath === "/en/suppliers" ||
    footerPath === "/agente-retail-ia" ||
    footerPath === "/en/retail-ai-agent" ||
    footerPath === "/que-es-el-agente-retail-ia" ||
    footerPath === "/en/what-is-the-retail-ai-agent" ||
    footerPath === "/" || footerPath === "/en" || footerPath === "/go" ||
    footerPath === "/en/go" ||
    footerPath === "/consejos" ||
    footerPath === "/en/tips" ||
    footerPath === "/casos-reales" ||
    footerPath === "/en/real-cases" ||
    footerPath === "/marketing" ||
    footerPath === "/musica-para-tu-local" ||
    footerPath === "/panel/subvenciones" ||

    footerPath === "/subvenciones" ||
    footerPath === "/recursos/subvenciones" ||
    footerPath === "/en/resources/subsidies" ||
    footerPath.startsWith("/subvenciones/") ||
    footerPath === "/en/grants" ||
    footerPath.startsWith("/en/grants/");

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <DeferredToasters />
          <LanguageSync />
          <PointerEventsGuard />
          <ScrollToTop />
          <PostAuthRouter />
          <CookieConsent />
          {withShell ? (
            <PublicShell>
              <Outlet />
              {!hideGlobalFooter && <InternalFooter />}
            </PublicShell>
          ) : (
            <>
              <Outlet />
              {!hideGlobalFooter && <InternalFooter />}
            </>
          )}
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  // Un trozo de página caducado no es un error real: recargamos con la versión
  // nueva en vez de mostrar la pantalla de fallo.
  const errMessage = error instanceof Error ? error.message : String(error ?? "");
  const recovering = isStaleChunkError(errMessage) && tryRecoverFromStaleChunk();
  useEffect(() => {
    if (recovering) return;
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error, recovering]);
  if (recovering) return <div className="min-h-screen bg-background" />;
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-xl font-bold text-foreground">This page didn't load</h1>
        <p className="mb-6 text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="rounded-md border border-border bg-card px-4 py-2 text-foreground"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // El subdominio del panel privado comparte despliegue con la web pública:
  // detectamos el host para servir `noindex, nofollow` solo ahí y evitar que
  // Google canibalice la marca con URLs del panel.
  loader: ({ location }): { isIndexableHost: boolean; isPrivatePath: boolean } => ({
    isIndexableHost: detectIndexableHost() === true,
    isPrivatePath: isPrivatePath(location.pathname),
  }),
  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "google-site-verification", content: "r5hvENUIy4IYzSABYqd79BTjf9BCCBO1L5bkhSy3tQs" },
      // Nota SEO: aquí NO se declaran title/description/og:*/twitter:*.
      // La única fuente de esas etiquetas es el componente <SEO /> (Helmet,
      // también en SSR) o el head() de cada ruta vía `pageHead()`.
      // Mantener defaults aquí duplicaba cada etiqueta en el HTML servido.
      ...(!loaderData?.isIndexableHost || loaderData?.isPrivatePath
        ? [
            { name: "robots", content: "noindex, nofollow" },
            { name: "googlebot", content: "noindex, nofollow" },
          ]
        : [{ name: "googlebot", content: "index, follow" }]),

      { name: "author", content: "Retail Lemon" },
      { name: "rating", content: "general" },
      { name: "theme-color", content: "#FACC15" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Club Pro" },
      { name: "format-detection", content: "telephone=no" },

    ],
    links: [
      {
        rel: "preload",
        as: "image",
        href: "/persona-hero-620.avif",
        imageSrcSet:
          "/persona-hero-620.avif 620w, /persona-hero-760.avif 760w, /persona-hero.avif 1248w",
        imageSizes: "(max-width: 640px) 102vw, min(44vw, 750px)",
        type: "image/avif",
        fetchPriority: "high",
      },

      {
        rel: "preload",
        href: "/fonts/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      // Cormorant es la fuente del H1: se precarga para que no quede al final
      // de la cadena crítica (antes se descubría tras parsear el CSS).
      {
        rel: "preload",
        href: "/fonts/co3umX5slCNuHLi8bLeY9MK7whWMhyjypVO7abI26QOD_hg9GnM.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },


      // Google exige un favicon cuadrado de al menos 48x48 para los resultados
      // de búsqueda; el de 192 es el que usa.
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { id: "lemon-manifest", rel: "manifest", href: "/manifest.json" },
    ],
    styles: [{ children: FONT_FACE_CSS }],
    scripts: [
      { children: SW_KILLSWITCH_SCRIPT },
      { children: CONSENT_DEFAULT_SCRIPT },
      { children: ANALYTICS_SCRIPT },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});
